/*
  God's Work colour swatches on product cards.

  Pressing a chip swaps the card photo to that colour's variant and re-points
  the card's links at that variant, so opening the product lands on the right
  colour and the correct variant is what gets added to cart.

  This file is referenced once per rendered card, so the script tag can appear
  many times on a collection page. A custom element is used deliberately: the
  define() call is guarded, and each element boots itself exactly once, so
  repeated execution is harmless no matter how many cards are on the page.
*/
(function () {
  if (window.customElements && customElements.get('gw-card-swatches')) return;

  class GwCardSwatches extends HTMLElement {
    connectedCallback() {
      if (this.dataset.gwBooted) return;
      this.dataset.gwBooted = '1';

      this.addEventListener('click', (event) => {
        const button = event.target.closest('.gw-card-swatch');
        if (!button || !this.contains(button)) return;
        event.preventDefault();

        /* Pressing the chip that is already on clears it. Photo, hover image,
           link and ring all reset together — a half-reset (picture back but
           ring still lit) reads as a bug. */
        if (button.classList.contains('gw-card-swatch--on')) {
          this.reset();
        } else {
          this.select(button);
        }
      });
    }

    /* Remembers the card's own photo/link once, before anything is changed. */
    remember(card) {
      card.querySelectorAll('.card__media img').forEach((image) => {
        if (image.dataset.gwOriginalSrc === undefined) {
          image.dataset.gwOriginalSrc = image.getAttribute('src') || '';
          image.dataset.gwOriginalSrcset = image.getAttribute('srcset') || '';
        }
      });
      card.querySelectorAll('a.full-unstyled-link').forEach((link) => {
        if (link.dataset.gwOriginalHref === undefined) {
          link.dataset.gwOriginalHref = link.getAttribute('href') || '';
        }
      });
    }

    reset() {
      const card = this.closest('.card-wrapper');
      if (!card) return;

      card.querySelectorAll('.card__media img').forEach((image) => {
        if (image.dataset.gwOriginalSrc === undefined) return;
        if (image.dataset.gwOriginalSrcset) {
          image.srcset = image.dataset.gwOriginalSrcset;
        } else {
          image.removeAttribute('srcset');
        }
        image.src = image.dataset.gwOriginalSrc;
      });

      card.querySelectorAll('a.full-unstyled-link').forEach((link) => {
        if (link.dataset.gwOriginalHref !== undefined) {
          link.setAttribute('href', link.dataset.gwOriginalHref);
        }
      });

      this.querySelectorAll('.gw-card-swatch').forEach((chip) => {
        chip.classList.remove('gw-card-swatch--on');
        chip.removeAttribute('aria-current');
      });
    }

    select(button) {
      const card = this.closest('.card-wrapper');
      if (!card) return;
      this.remember(card);

      /* Dawn renders TWO images per card: the featured one and a second that
         is revealed on hover (.media--hover-effect). The cursor is necessarily
         over the card when a chip is clicked, so leaving the hover image alone
         means the shopper keeps seeing the OLD colour and the swap looks
         broken. Both images are pointed at the chosen variant. */
      const srcset = (button.dataset.gwSrcset || '').replace(/\s+/g, ' ').trim();
      const src = button.dataset.gwSrc;
      card.querySelectorAll('.card__media img').forEach((image) => {
        if (srcset) image.srcset = srcset;
        if (src) image.src = src;
      });

      /* Dawn renders the title link twice (card and standard-card layouts).
         Only those point at the product, so only those are re-pointed. */
      const url = button.dataset.gwUrl;
      if (url) {
        card.querySelectorAll('a.full-unstyled-link').forEach((link) => {
          link.setAttribute('href', url);
        });
      }

      this.querySelectorAll('.gw-card-swatch').forEach((chip) => {
        const on = chip === button;
        chip.classList.toggle('gw-card-swatch--on', on);
        if (on) {
          chip.setAttribute('aria-current', 'true');
        } else {
          chip.removeAttribute('aria-current');
        }
      });
    }
  }

  customElements.define('gw-card-swatches', GwCardSwatches);
})();
